import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Link, Box, Tooltip, Dialog } from '@material-ui/core';
import DateRangeIcon from '@material-ui/icons/DateRange';
import InfoIcon from '@material-ui/icons/Info';
import LanguageIcon from '@material-ui/icons/Language';
import HelpIcon from '@material-ui/icons/Help';

import TextWithLabel from 'components/shared/TextWithLabel';
import { StyledIconButton, SlideTransition } from 'components/shared/MuiExtensions';
import { RootState } from 'state/store';
import { filteredHolidays } from 'utils/functions';
import { Agent } from 'typings';

import BusinessHours from './BusinessHours';
import TeamHolidays from './TeamHolidays';
import FAQs from './FAQs';

const normalizeUrl = (url: string) => {
  if (!url) return '';

  return !url.startsWith('http') ? `http://${url}` : url;
};

const getAddress = (customer: Agent) => {
  if (!customer || !customer.street) return 'N/A';

  let address = customer.street;
  if (customer.city) {
    address += `, ${customer.city}`;
  }
  if (customer.state) {
    address += `, ${customer.state}`;
  }
  if (customer.state && customer.postalCode) {
    address += ` ${customer.postalCode}`;
  }

  return address;
};

const Wrapper = styled('div')`
  & > * + * {
    border-top: 1px solid ${(props) => props.theme.palette.grey['200']};
  }

  & > .MuiGrid-container {
    & > .MuiGrid-grid-sm-6 + .MuiGrid-grid-sm-6 {
      border-left: 1px solid ${(props) => props.theme.palette.grey['200']};
    }
  }
`;

const CustomerDetails = () => {
  const customer: Agent | undefined = useSelector((state: RootState) => state.lead.entity?.agent);
  const [showFAQ, setShowFAQ] = useState(false);
  const descriptionElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showFAQ) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [showFAQ]);

  if (!customer) {
    return null;
  }

  const showBusinessHours =
    customer?.agentSetting?.businessHours?.hours && customer?.agentSetting?.businessHours?.enabled;
  const customerTimezone = customer?.agentSetting?.settings?.general?.timezone ?? '';
  const holidays = filteredHolidays(customer?.team?.teamHolidays, customerTimezone);
  const hasHolidays = !!holidays?.length;

  const handleOpenFAQ = () => {
    setShowFAQ(true);
  };

  const handleCloseFAQ = () => {
    setShowFAQ(false);
  };

  return (
    <Wrapper>
      <div className="horizontal-space">
        <TextWithLabel label="name">{customer?.personaTeamName}</TextWithLabel>
        <Box display="flex" alignItems="flex-start">
          {!!showBusinessHours && hasHolidays && (
            <TeamHolidays holidays={customer?.team?.teamHolidays} timezone={customerTimezone} />
          )}
          {!!showBusinessHours && (
            <BusinessHours
              hours={customer?.agentSetting?.businessHours?.hours}
              timezone={customerTimezone}
            />
          )}
          {customer?.calendly && (
            <StyledIconButton>
              <Link
                href={normalizeUrl(customer?.calendly)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DateRangeIcon />
              </Link>
            </StyledIconButton>
          )}
          {customer?.website && (
            <Tooltip title={normalizeUrl(customer?.website)} placement="top" arrow>
              <StyledIconButton>
                <Link
                  href={normalizeUrl(customer?.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LanguageIcon />
                </Link>
              </StyledIconButton>
            </Tooltip>
          )}
          {customer?.internalUrl && (
            <Tooltip title="Customer Information" placement="top" arrow>
              <StyledIconButton>
                <Link href={customer?.internalUrl} target="_blank" rel="noopener noreferrer">
                  <InfoIcon />
                </Link>
              </StyledIconButton>
            </Tooltip>
          )}
          {!!customer?.faqEntries?.length && (
            <Tooltip title="FAQs" placement="top" arrow>
              <StyledIconButton onClick={handleOpenFAQ}>
                <HelpIcon color="secondary" />
              </StyledIconButton>
            </Tooltip>
          )}
        </Box>
      </div>
      <TextWithLabel label="address">{getAddress(customer)}</TextWithLabel>
      <TextWithLabel label="concierge identity">
        {customer?.personaName}, {customer?.personaTitle}
      </TextWithLabel>
      {!!customer?.faqEntries?.length && (
        <Dialog
          fullWidth={true}
          maxWidth="md"
          open={showFAQ}
          onClose={handleCloseFAQ}
          TransitionComponent={SlideTransition}
          aria-labelledby="faq-dialog"
          scroll="paper"
        >
          <div id="scroll-dialog-description" ref={descriptionElementRef} tabIndex={-1}>
            <FAQs entries={customer?.faqEntries} customerName={customer?.personaTeamName} />
          </div>
        </Dialog>
      )}
    </Wrapper>
  );
};

export default CustomerDetails;
